/**
 * S3 数据异常工作台 Tab(T39 / T40 / T41 / T42 / T44)
 *
 * 承接:
 *   - T40 异常清单(紧迫度倒序 + 类型筛选)
 *   - T41 "定位 / 忽略" 行内动作(定位 → 跳 S4 反查页带 query)
 *   - T42 忽略对话框(必填理由)
 *   - T44 无权限只读模式
 *
 * 2026-07-06 规则精简:只保留规则 1(燃油漏录)/ 2(该有的费用没出现),
 * 规则 3/4/5/6 退役 — "去归类"入口随规则 3 一并移除,
 * 退役规则的历史记录(已解决/已忽略)仍可查看。
 *
 * 数据源:GET /v3/settlement/anomalies
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    Table,
    Tag,
    Space,
    Button,
    Modal,
    Input,
    Select,
    Form,
    Tooltip,
    message,
    Alert,
    Empty,
    Typography,
    Spin,
    Badge,
} from "antd";
import {
    ExclamationCircleOutlined,
    EyeOutlined,
    StopOutlined,
    ReloadOutlined,
    SettingOutlined,
    UndoOutlined,
    AimOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../../../hooks/useAuth";
import settlementApi from "../../../../api/settlementApi";
import RuleConfigSidebar from "./RuleConfigSidebar";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const SEVERITY_COLORS = { HIGH: "red", MEDIUM: "orange", LOW: "green" };
const SEVERITY_LABELS = { HIGH: "高", MEDIUM: "中", LOW: "低" };

// 在用规则(表头筛选只列这两个)
const ACTIVE_RULE_LABELS = {
    rule1_fuel_missing: "燃油可能漏录",
    rule2_expected_missing: "该有的费用没出现",
};

// 含退役规则的完整映射 — 历史记录(已解决/已忽略)展示规则名用
const RULE_LABELS = {
    ...ACTIVE_RULE_LABELS,
    rule3_unrecognized: "出现新的费用写法(已停用)",
    rule4_amount_outlier: "费用金额异常(已停用)",
    rule5_frozen_vs_live: "报表对不上(已停用)",
    rule6_flight_hours_quality: "飞行小时 BH/FH(已停用)",
};

const DataAnomalyTab = ({ onCountsChange }) => {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const canWrite = hasPermission("settlement:exception:write");
    const canAdminRule = hasPermission("settlement:rule:admin");

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [countsBySeverity, setCountsBySeverity] = useState({});
    const [filters, setFilters] = useState({
        status: "OPEN",
        severity: undefined,
        rule_id: undefined,
    });

    // 忽略对话框
    const [ignoreTarget, setIgnoreTarget] = useState(null);
    const [ignoreReason, setIgnoreReason] = useState("");
    const [ignoreLoading, setIgnoreLoading] = useState(false);

    // 撤回忽略对话框(2026-05-13 决策 13)
    const [unignoreTarget, setUnignoreTarget] = useState(null);
    const [unignoreNote, setUnignoreNote] = useState("");
    const [unignoreLoading, setUnignoreLoading] = useState(false);

    // 规则配置侧边栏
    const [ruleSidebarOpen, setRuleSidebarOpen] = useState(false);

    // 手动扫描
    const [scanLoading, setScanLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await settlementApi.dataQuality.listAnomalies({
                status: filters.status,
                severity: filters.severity,
                rule_id: filters.rule_id,
                page: pagination.current,
                page_size: pagination.pageSize,
            });
            const nextItems = res.data.items || [];
            const total = res.data.pagination?.total || 0;

            // 处理完当前页所有条目(或总数变少)后,当前页可能超出实际页数,
            // 返回空页会被误读成"全部处理完了" — 自动回退到最后一个有数据的页,
            // 页码变化会触发 effect 重新加载。
            const lastPage = Math.max(1, Math.ceil(total / pagination.pageSize));
            if (nextItems.length === 0 && total > 0 && pagination.current > lastPage) {
                setPagination((prev) => ({ ...prev, current: lastPage, total }));
                return;
            }

            setItems(nextItems);
            setPagination((prev) => ({ ...prev, total }));
            const cbs = res.data.counts_by_severity || {};
            setCountsBySeverity(cbs);
            if (onCountsChange) onCountsChange(cbs);
        } catch (err) {
            console.error("加载异常清单失败:", err);
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("加载异常清单失败: " + msg);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.current, pagination.pageSize, onCountsChange]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pagination.current, pagination.pageSize]);

    // T41 — 跳 S4 反查页定位到对应费用
    //   2026-05-13 决策 14:统一走"定位"行为,让用户自己看清再决定下一步动作。
    //   参数 _action 仅作为审计语义保留(locate / view),不再影响目的页行为。
    //   注:BH_FH / LINE: / rule3 分支服务于退役规则(3/6)的历史记录「查看」。
    const handleJumpToLegCost = (record, _action) => {
        const params = new URLSearchParams();
        if (record.flight_operation_id) params.set("flight_operation_id", record.flight_operation_id);
        if (record.cost_type === "BH_FH") {
            // 退役 rule6 BH<FH 矛盾:cost_type 是合成值,leg-cost 主表不认识;只按航段定位即可
        } else if (record.cost_type && !record.cost_type.startsWith("LINE:")) {
            params.set("cost_type", record.cost_type);
        } else if (record.rule_id === "rule3_unrecognized") {
            // 退役规则 3 没具体 cost_type,跳过去时默认按 OTHERS_FEE 定位
            params.set("cost_type", "OTHERS_FEE");
        }
        if (record.batch_id) params.set("batch_id", record.batch_id);
        params.set("from", "anomaly");
        navigate(`/settlement/leg-cost?tab=leg-cost&${params.toString()}`);
    };

    // T42 — 忽略
    const openIgnoreModal = (record) => {
        setIgnoreTarget(record);
        setIgnoreReason("");
    };
    const confirmIgnore = async () => {
        const reason = (ignoreReason || "").trim();
        if (reason.length < 3) {
            message.warning("忽略理由至少 3 个字符");
            return;
        }
        setIgnoreLoading(true);
        try {
            await settlementApi.dataQuality.ignoreAnomaly(ignoreTarget.id, reason);
            message.success("已忽略;数据变化大时系统会自动重新激活");
            setIgnoreTarget(null);
            setIgnoreReason("");
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("忽略失败: " + msg);
        } finally {
            setIgnoreLoading(false);
        }
    };

    // 2026-05-13 决策 13 — 撤回忽略(IGNORED → OPEN)
    const openUnignoreModal = (record) => {
        setUnignoreTarget(record);
        setUnignoreNote("");
    };
    const confirmUnignore = async () => {
        if (!unignoreTarget) return;
        setUnignoreLoading(true);
        try {
            await settlementApi.dataQuality.unignoreAnomaly(
                unignoreTarget.id,
                unignoreNote.trim() || null
            );
            message.success("已撤回忽略,异常重新进入待处理");
            setUnignoreTarget(null);
            setUnignoreNote("");
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("撤回失败: " + msg);
        } finally {
            setUnignoreLoading(false);
        }
    };

    // 手动扫描
    const handleManualScan = async () => {
        setScanLoading(true);
        try {
            const res = await settlementApi.dataQuality.triggerAnomalyScan({ scope: "all" });
            message.success(
                `扫描完成:新增 ${res.data.total_new} 条,重新激活 ${res.data.total_reactivated} 条,自动关闭 ${res.data.auto_resolved_count} 条`
            );
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("扫描失败: " + msg);
        } finally {
            setScanLoading(false);
        }
    };

    // 行内动作映射
    //   - OPEN     : 「定位」(跳 S4 反查页看清再决定) + 「忽略」
    //   - IGNORED  : 已业务确认过,只显示「查看」+「撤回忽略」
    //   - RESOLVED : 问题已解决,只能查看
    //   - 无权限   : 只读
    const renderActions = (record) => {
        if (!canWrite) {
            return <Text type="secondary">只读</Text>;
        }
        const actions = [];

        if (record.status === "OPEN") {
            // 2026-05-13 决策 14:原「去录入」改「定位」,让用户先看再决定动作
            actions.push(
                <Button key="locate" size="small" type="link" icon={<AimOutlined />} onClick={() => handleJumpToLegCost(record, "locate")}>
                    定位
                </Button>
            );
            actions.push(
                <Button key="ignore" size="small" type="link" danger icon={<StopOutlined />} onClick={() => openIgnoreModal(record)}>
                    忽略
                </Button>
            );
        } else {
            // IGNORED / RESOLVED — 仅查看
            actions.push(
                <Button key="view" size="small" type="link" icon={<EyeOutlined />} onClick={() => handleJumpToLegCost(record, "view")}>
                    查看
                </Button>
            );
            // IGNORED 额外提供「撤回忽略」(RESOLVED 不提供 — 已经解决了不应回退)
            if (record.status === "IGNORED") {
                actions.push(
                    <Button key="unignore" size="small" type="link" icon={<UndoOutlined />} onClick={() => openUnignoreModal(record)}>
                        撤回忽略
                    </Button>
                );
            }
        }

        return <Space size="small">{actions}</Space>;
    };

    const columns = [
        {
            title: "紧迫度",
            dataIndex: "severity",
            key: "severity",
            width: 80,
            align: "center",
            filters: [
                { text: "高(红)", value: "HIGH" },
                { text: "中(黄)", value: "MEDIUM" },
                { text: "低(绿)", value: "LOW" },
            ],
            filterMultiple: false,
            // 受控:表头筛选状态与实际请求参数保持一致,避免重挂载后 UI 显示"未筛选"
            // 但请求仍带条件的脱节
            filteredValue: filters.severity ? [filters.severity] : null,
            render: (v) => (
                <Tag color={SEVERITY_COLORS[v]} style={{ fontWeight: 600 }}>
                    {SEVERITY_LABELS[v] || v}
                </Tag>
            ),
        },
        {
            title: "规则",
            dataIndex: "rule_id",
            key: "rule_id",
            width: 140,
            // 筛选项只列在用规则;历史记录里的退役规则名仍能正常显示(RULE_LABELS 全量映射)
            filters: Object.entries(ACTIVE_RULE_LABELS).map(([k, v]) => ({ text: v, value: k })),
            filterMultiple: false,
            filteredValue: filters.rule_id ? [filters.rule_id] : null,
            render: (v) => RULE_LABELS[v] || v,
        },
        {
            title: "航班",
            key: "flight",
            width: 180,
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{r.flight_number || "—"}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {r.departure_airport || "?"} → {r.arrival_airport || "?"}
                    </Text>
                </Space>
            ),
        },
        {
            title: "异常描述",
            key: "title",
            ellipsis: { showTitle: false },
            render: (_, r) => (
                <Tooltip
                    placement="topLeft"
                    title={
                        <Paragraph style={{ color: "#fff", marginBottom: 0, whiteSpace: "pre-line" }}>
                            {r.description}
                        </Paragraph>
                    }
                >
                    <Space direction="vertical" size={0}>
                        <Text>{r.title}</Text>
                        {r.unrecognized_original_text && (
                            <Tag color="orange" style={{ fontSize: 11 }}>
                                原始写法:{r.unrecognized_original_text}
                            </Tag>
                        )}
                        {r.reactivated_count > 0 && (
                            <Tag color="purple" style={{ fontSize: 11 }}>
                                曾被重新激活 {r.reactivated_count} 次
                            </Tag>
                        )}
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: "批次",
            dataIndex: "batch_id",
            key: "batch_id",
            width: 100,
            align: "center",
            render: (v) => (v ? `#${v}` : "—"),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (v) => {
                const color = v === "OPEN" ? "blue" : v === "IGNORED" ? "default" : "success";
                const label = v === "OPEN" ? "待处理" : v === "IGNORED" ? "已忽略" : "已解决";
                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: "最近检测",
            dataIndex: "last_detected_at",
            key: "last_detected_at",
            width: 150,
            // 后端返回 UTC(带 Z);dayjs 解析后按浏览器本地时区显示,
            // 之前直接裁字符串显示的是 UTC,差 8 小时
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "—"),
        },
        {
            title: "操作",
            key: "actions",
            fixed: "right",
            width: 200,
            render: (_, r) => renderActions(r),
        },
    ];

    const handleTableChange = (newPagination, tableFilters) => {
        const newSev = tableFilters.severity?.[0];
        const newRule = tableFilters.rule_id?.[0];
        const filterChanged = newSev !== filters.severity || newRule !== filters.rule_id;
        setPagination({
            // 筛选条件变化时显式回到第 1 页,不依赖 antd 的内部重置行为
            current: filterChanged ? 1 : newPagination.current,
            pageSize: newPagination.pageSize,
            total: newPagination.total,
        });
        if (filterChanged) {
            setFilters((prev) => ({ ...prev, severity: newSev, rule_id: newRule }));
        }
    };

    const statusOptions = useMemo(
        () => [
            { value: "OPEN", label: "待处理" },
            { value: "IGNORED", label: "已忽略" },
            { value: "RESOLVED", label: "已解决" },
            { value: "ALL", label: "全部" },
        ],
        []
    );

    return (
        <div style={{ padding: 16 }}>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="数据异常工作台 — 系统每天自动扫描;每条点对应动作处理,处理完自动从清单消失"
                description={
                    <Space split="|">
                        <span>
                            高(红):
                            <Badge count={countsBySeverity.HIGH || 0} showZero style={{ backgroundColor: "#ff4d4f", marginLeft: 4 }} />
                        </span>
                        <span>
                            中(黄):
                            <Badge count={countsBySeverity.MEDIUM || 0} showZero style={{ backgroundColor: "#faad14", marginLeft: 4 }} />
                        </span>
                        <span>
                            低(绿):
                            <Badge count={countsBySeverity.LOW || 0} showZero style={{ backgroundColor: "#52c41a", marginLeft: 4 }} />
                        </span>
                    </Space>
                }
            />

            <Space style={{ marginBottom: 12 }} wrap>
                <Select
                    value={filters.status}
                    style={{ width: 130 }}
                    options={statusOptions}
                    onChange={(v) => {
                        setFilters((prev) => ({ ...prev, status: v }));
                        setPagination((p) => ({ ...p, current: 1 }));
                    }}
                />
                {canWrite && (
                    <Button icon={<ReloadOutlined />} loading={scanLoading} onClick={handleManualScan}>
                        立即扫描
                    </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                    刷新
                </Button>
                {canAdminRule && (
                    <Button icon={<SettingOutlined />} onClick={() => setRuleSidebarOpen(true)}>
                        规则设置
                    </Button>
                )}
            </Space>

            <Spin spinning={loading}>
                {/* Table 常驻:空结果时不能整个替换成 Empty,否则表头筛选和分页器一起消失,
                    用户既无法取消筛选也无法翻页(会被"锁死"在空态) */}
                <Table
                    rowKey="id"
                    size="small"
                    columns={columns}
                    dataSource={items}
                    locale={{
                        emptyText: <Empty description="当前没有符合筛选条件的异常 — 棒!" />,
                    }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                />
            </Spin>

            {/* T42 — 忽略对话框 */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                        <span>忽略异常</span>
                    </Space>
                }
                open={!!ignoreTarget}
                onCancel={() => {
                    if (!ignoreLoading) {
                        setIgnoreTarget(null);
                        setIgnoreReason("");
                    }
                }}
                onOk={confirmIgnore}
                okText="确认忽略"
                cancelText="取消"
                okButtonProps={{ loading: ignoreLoading }}
                destroyOnClose
            >
                {ignoreTarget && (
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Alert
                            type="info"
                            message={ignoreTarget.title}
                            description={
                                <Text type="secondary" style={{ whiteSpace: "pre-line" }}>
                                    忽略后,如本航段该费用类型金额变化超阈值,系统会自动重新激活,
                                    提示文案带「原忽略理由可能不再适用」。
                                </Text>
                            }
                        />
                        <Form.Item required style={{ marginBottom: 0 }} label="忽略理由(必填)">
                            <TextArea
                                value={ignoreReason}
                                onChange={(e) => setIgnoreReason(e.target.value)}
                                rows={3}
                                placeholder="例如:本批次确认无燃油费用,飞机在 NQZ 停场加油"
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    </Space>
                )}
            </Modal>

            {/* 2026-05-13 决策 13 — 撤回忽略对话框 */}
            <Modal
                title={
                    <Space>
                        <UndoOutlined style={{ color: "#1890ff" }} />
                        <span>撤回忽略</span>
                    </Space>
                }
                open={!!unignoreTarget}
                onCancel={() => {
                    if (!unignoreLoading) {
                        setUnignoreTarget(null);
                        setUnignoreNote("");
                    }
                }}
                onOk={confirmUnignore}
                okText="确认撤回"
                cancelText="取消"
                okButtonProps={{ loading: unignoreLoading, type: "primary" }}
                destroyOnClose
            >
                {unignoreTarget && (
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Alert
                            type="warning"
                            message={unignoreTarget.title}
                            description={
                                <Text type="secondary" style={{ whiteSpace: "pre-line" }}>
                                    撤回后异常会回到「待处理」状态,请重新评估并处理。
                                    {unignoreTarget.ignore_reason ? (
                                        <>{"\n"}原忽略理由:{unignoreTarget.ignore_reason}</>
                                    ) : null}
                                </Text>
                            }
                        />
                        <Form.Item style={{ marginBottom: 0 }} label="撤回备注(选填)">
                            <TextArea
                                value={unignoreNote}
                                onChange={(e) => setUnignoreNote(e.target.value)}
                                rows={3}
                                placeholder="例如:重新核对发现该费用确实漏录,需要补"
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    </Space>
                )}
            </Modal>

            {/* T43 — 规则设置侧边栏(仅 settlement:rule:admin) */}
            <RuleConfigSidebar
                open={ruleSidebarOpen}
                onClose={() => setRuleSidebarOpen(false)}
                onSaved={load}
            />
        </div>
    );
};

export default DataAnomalyTab;
